import { useState } from 'react';
import { Select } from 'antd';
import { useDebounce } from '@/shared/lib/hooks';

export interface RemoteSearchSelectProps<T> {
  value?: string;
  onChange: (value: string | undefined, option?: T) => void;
  /** Wire your own RTK Query hook to the (debounced) search text. */
  fetch: (search: string) => { data?: T[]; isFetching: boolean };
  getOptionValue: (item: T) => string;
  getOptionLabel: (item: T) => string;
  minSearchLength?: number;
  placeholder?: string;
  size?: 'small' | 'middle';
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/** A debounced, server-searched `Select` — the `showSearch + onSearch + filterOption={false}`
 *  boilerplate that several screens (product kit/substitutes editors, this one) each hand-roll. */
export function RemoteSearchSelect<T>({
  value,
  onChange,
  fetch,
  getOptionValue,
  getOptionLabel,
  minSearchLength = 2,
  placeholder,
  size,
  disabled,
  className,
  style,
}: RemoteSearchSelectProps<T>) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const { data, isFetching } = fetch(debouncedSearch.length >= minSearchLength ? debouncedSearch : '');
  const items = data ?? [];

  return (
    <Select<string>
      showSearch
      allowClear
      value={value}
      placeholder={placeholder}
      size={size}
      disabled={disabled}
      className={className}
      style={style}
      searchValue={search}
      onSearch={setSearch}
      filterOption={false}
      loading={isFetching}
      notFoundContent={null}
      onChange={(next) => {
        if (!next) {
          onChange(undefined);
          return;
        }
        const option = items.find((item) => getOptionValue(item) === next);
        onChange(next, option);
      }}
      options={items.map((item) => ({ value: getOptionValue(item), label: getOptionLabel(item) }))}
    />
  );
}
